import { Component, Input, ViewChild } from '@angular/core';
import { AlertController, IonAccordionGroup, IonCheckbox, IonInput, IonToggle } from '@ionic/angular';
import { SpartaxAuftragskunde, SpartaxAuftragskundeArt, SpartaxEinkunftsArt, SpartaxErklaerungsjahr, SpartaxSchaetzungsJahreseinkunftArt } from 'src/app/data/dto/spartax/spartax-auftragskunde.dto';
import { SpartaxFormModelDtoUtil } from 'src/app/data/dto/spartax/spartax-form-model.dto';
import { SpartaxFrage, SpartaxFragenfunktionKeys, SpartaxFragenkategorie } from 'src/app/data/dto/spartax/spartax-frage.dto';
import { FormEventsService } from 'src/app/data/service/form-events.service';
import { from } from "linq-to-typescript";
import * as moment from 'moment';
import { SpartaxFormStepBaseComponent } from '../spartax-form-step-base';

@Component({
  selector: 'app-spartax-additional-questions',
  templateUrl: './spartax-additional-questions.component.html',
  styleUrls: ['./spartax-additional-questions.component.scss'],
})
export class SpartaxAdditionalQuestionsComponent extends SpartaxFormStepBaseComponent {
  @Input()
  questions: SpartaxFrage[];

  @Input()
  partnerEventsLinkUrl: string;

  @Input()
  debug: boolean;

  @Input()
  set overviewMode(value: boolean | string | any) {
    this._overviewMode = value === true || value === 'true';
  }

  get overviewMode(): boolean {
    return this._overviewMode;
  }

  @Input()
  set cqMode(value: boolean | string | any) {
    this._cqMode = value === true || value === 'true';
  }

  get cqMode(): boolean {
    return this._cqMode;
  }

  @ViewChild('categoriesAccordionGroup')
  categoriesAccordionGroup!: IonAccordionGroup;

  private _cqMode = false;
  private _overviewMode = false;
  private overtakePromptsPresented: string[] = [];
  private checkboxStateOverride = false;

  public visible = false;
  public questionCategories: SpartaxFragenkategorie[] = [];
  public relevantQuestions: SpartaxFrage[] = [];
  public questionByCategoryId: { [key: number]: SpartaxFrage[]; } = {};
  public openHelpContainer: number[] = [];
  public expandedCategories: string[] = [];

  constructor(
    private formEventService: FormEventsService,
    private alertController: AlertController,
  ) {
    super();
  }

  override onShowFormStep() {
    this.relevantQuestions = from(this.questions).where(x => this.isQuestionRelevant(x)).toArray();

    if (this.overviewMode) {
      const answeredQuestions = from(this.model.auftragskunden)
        .selectMany(x => x.Erklaerungsjahre)
        .selectMany(x => x.FragenAntworten)
        .where(x => !!x.Antwort)
        .select(x => x.FrageListItemId)
        .distinct()
        .toArray();

      this.relevantQuestions = from(this.relevantQuestions).where(x => answeredQuestions.includes(x.ListItemId)).toArray();
    }

    this.questionCategories = from(this.relevantQuestions)
      .groupBy(x => x.Fragenkategorie.ListItemId)
      .select(x => x.first().Fragenkategorie)
      .orderBy(x => x.Reihenfolge)
      .toArray();

    for (const category of this.questionCategories) {
      this.questionByCategoryId[category.ListItemId] = from(this.relevantQuestions)
        .where(x => x.Fragenkategorie.ListItemId === category.ListItemId)
        .orderBy(x => x.Reihenfolge, ((a, b) => a - b))
        .toArray();
    }

    if (this.overviewMode || this.cqMode) {
      this.expandedCategories = this.questionCategories.map(x => x.ListItemId + '');
    }

    if (this.cqMode && !this.model.cqAdditionalQuestionsAnswers) {
      this.model.cqAdditionalQuestionsAnswers = {};
    }

    this.visible = true;
  }

  override onHideFormStep() {
    this.visible = false;
  }

  override formIsValid(): boolean {
    if (super.formIsValid()) {
      if (this.cqMode) {
        for (const question of this.relevantQuestions) {
          const allAuftragskunden = this.getAuftragskundenForQuestion(question);
          for (const auftragskunde of allAuftragskunden) {
            const cqKey = `${question.ListItemId}_${auftragskunde.KundeFormModelId}`;
            if (this.model.cqAdditionalQuestionsAnswers && this.model.cqAdditionalQuestionsAnswers[cqKey] == null) {
              return false;
            }
          }
        }
      }
      return true;
    }
  }

  toggleHelpContainer(question: SpartaxFrage) {
    if (this.openHelpContainer.includes(question.ListItemId)) {
      this.openHelpContainer = this.openHelpContainer.filter(x => x !== question.ListItemId);
    } else {
      this.openHelpContainer.push(question.ListItemId);
    }
  }

  isQuestionRelevant(question: SpartaxFrage) {
    return from(this.model.auftragskunden).any(x => this.model.hideNotRelevantAdditionalQuestions ? this.isAuftragskundeRowVisible(question, x) : true);
  }

  isAuftragskundeRowVisible(question: SpartaxFrage, auftragskunde: SpartaxAuftragskunde) {
    for (let erklaerungsjahr of auftragskunde.Erklaerungsjahre) {
      if (this.isYearRelevant(question, erklaerungsjahr.Jahr, auftragskunde)) {
        return true;
      }
    }
  }

  isYearRelevant(question: SpartaxFrage, year: number | string, auftragskunde: SpartaxAuftragskunde, usePreviousOrderData = false) {
    if (this.checkboxStateOverride) {
      return false;
    }

    if (this.cqMode && from(this.questions).where(x => x.KundeFragebogen).select(x => x.ListItemId).contains(question.ListItemId) == false) {
      return false;
    }

    const yearStr = year.toString();
    let relevant = false;

    const erklaerungsjahre = usePreviousOrderData ? auftragskunde.ErklaerungsjahreAlt || [] : auftragskunde.Erklaerungsjahre;
    const einkunftsartenOfThisYear = from(erklaerungsjahre).firstOrDefault(x => x.Jahr.toString() === yearStr)?.EinkunftsArten;

    //If overview mode, year is relevant if customer answered it
    if (this.overviewMode) {
      return from(erklaerungsjahre)
        .where(x => x.Jahr.toString() === yearStr)
        .selectMany(x => x.FragenAntworten)
        .where(x => !!x.Antwort && x.FrageListItemId === question.ListItemId)
        .any();
    }

    //If answered previously, year is relevant
    if (usePreviousOrderData && this.isQuestionAnsweredInPreviousYear(question, year, auftragskunde)) {
      return true;
    }

    //Check if person lived in this year
    if (auftragskunde.Kunde.VerstorbenAm && +year > moment(auftragskunde.Kunde.VerstorbenAm).year()) {
      return false;
    }

    //check if year is within range
    if (!from(erklaerungsjahre).any(x => x.Jahr === year)) {
      return false;
    }

    //if wiederaufnahme was confirmed, year is relevant
    const erklaerungsjahr = from(erklaerungsjahre).firstOrDefault(x => x.Jahr === year);
    if (erklaerungsjahr?.Wiederaufnahme) {
      const answer = this.getQuestionAnswer(question, erklaerungsjahr);
      if (answer) {
        return true;
      }
    }

    //Check relevance based on question functions
    if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F123)) {
      if (usePreviousOrderData) {
        return true;
      } else {
        //if relevant in previous year, it is not relevant in this year
        return !this.isYearRelevant(question, year, auftragskunde, true);
      }
    }

    if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F122)) {
      if (!this.model.kinder.length) {
        return false;
      }
    }

    if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F128)) {
      //question is relevant if it was answered in previous order
      if (usePreviousOrderData && this.isQuestionAnsweredInPreviousYear(question, year, auftragskunde)) {
        return true;
      }

      //or if ANY answer in master question "Behinderung" is given
      for (const ak of this.model.auftragskunden) {
        if (ak.Kunde.BehinderungMitGutachten || ak.Kunde.Behindertenpass || ak.Kunde.Parkausweis || ak.Kunde.ErhoehteFamilienBeihilfe || 
          ak.Kunde.Pflegegeldbezug || ak.Pflegeaufwand || ak.Kunde.Behinderungen?.length > 0 || ak.FeststellungGradBehinderungNotwendig
        ) {
          return true;
        }
      }
      return false;
    }

    if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F100)) {
      const kind = SpartaxFormModelDtoUtil.getKindForAuftragskunde(this.model, auftragskunde);
      if (kind) {
        relevant = kind.InAusbildung;
      } else {
        relevant = false;
      }
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F108)) {
      relevant = einkunftsartenOfThisYear?.includes(SpartaxEinkunftsArt.pension);
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F109)) {
      relevant = einkunftsartenOfThisYear?.includes(SpartaxEinkunftsArt.dienstverhaeltnis);
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F110)) {
      const schaetzungJahreseinkunft = from(erklaerungsjahre)
        .firstOrDefault(x => x.Jahr.toString() === yearStr)?.SchaetzungJahreseinkunft;
      relevant = einkunftsartenOfThisYear?.includes(SpartaxEinkunftsArt.dienstverhaeltnis) &&
        (!!!schaetzungJahreseinkunft || schaetzungJahreseinkunft == SpartaxSchaetzungsJahreseinkunftArt.maximum);
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F111)) {
      relevant = einkunftsartenOfThisYear?.length > 0 && auftragskunde.AuftragsKundeArt !== SpartaxAuftragskundeArt.kind;
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F125)) {
      relevant = from(einkunftsartenOfThisYear).any(x => x === SpartaxEinkunftsArt.betrieb);
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F126)) {
      relevant = from(einkunftsartenOfThisYear).any(x => x === SpartaxEinkunftsArt.vermietung ||
        x === SpartaxEinkunftsArt.kapitalvermoegen);
    } else if (question.Fragenfunktionen && question.Fragenfunktionen.find(x => x.Key === SpartaxFragenfunktionKeys.F127)) {
      const customerIsNoChild = auftragskunde.AuftragsKundeArt !== SpartaxAuftragskundeArt.kind;
      if (customerIsNoChild) {
        if (usePreviousOrderData) {
          relevant = true;
        } else {
          //if relevant in previous year, it is not relevant in this year
          return !this.isYearRelevant(question, year, auftragskunde, true);
        }
      } else {
        relevant = false;
      }
    } else {
      relevant = from(einkunftsartenOfThisYear).any(x => x === SpartaxEinkunftsArt.dienstverhaeltnis || x === SpartaxEinkunftsArt.pension);
    }

    //Last check if question is relevant for this year
    if (relevant) {
      for (const questionRelevantYear of question.Jahre) {
        if (questionRelevantYear.toLowerCase().includes('alle') ||
          questionRelevantYear === yearStr) {
          return true;
        }
      }
    }

    return false;
  }

  isYearRelevantForAnyCustomer(question: SpartaxFrage, year: number | string) {
    for (let auftragskunde of this.model.auftragskunden) {
      if (this.isYearRelevant(question, year, auftragskunde)) {
        return true;
      }
    }
    return false;
  }

  onRelevantQuestionsToggleChanged(event: Event) {
    this.model.hideNotRelevantAdditionalQuestions = (event.target as unknown as IonToggle).checked;
    this.onShowFormStep();
  }

  async onQuestionAnswerSelected(e: Event, question: SpartaxFrage, year: SpartaxErklaerungsjahr, auftragskunde: SpartaxAuftragskunde) {
    const checked = (e.target as unknown as IonCheckbox).checked;
    this.setQuestionAnswer(checked, question, year);

    if (checked && this.isYearRelevant(question, year.Jahr + 1, auftragskunde)) {
      await this.showOvertakePopup(checked, question, year, auftragskunde);
    }
  }

  setQuestionAnswer(value: boolean, question: SpartaxFrage, year: SpartaxErklaerungsjahr) {
    if (!year.FragenAntworten) {
      year.FragenAntworten = [];
    }

    let antwort = year.FragenAntworten.find(x => x.FrageListItemId === question.ListItemId);

    if (!antwort) {
      antwort = {
        FrageListItemId: question.ListItemId,
      };
      year.FragenAntworten.push(antwort);
    }

    antwort.Antwort = value;
  }

  onQuestionCommentEntered(e: Event, question: SpartaxFrage, auftragskunde: SpartaxAuftragskunde) {
    if (!auftragskunde.Kommentare) {
      auftragskunde.Kommentare = []
    }

    let kommentar = auftragskunde.Kommentare.find(x => x.FrageListItemId === question.ListItemId);

    if (!kommentar) {
      kommentar = {
        FrageListItemId: question.ListItemId,
        Kommentar: '',
      };
      auftragskunde.Kommentare.push(kommentar);
    }

    kommentar.Kommentar = (e.target as unknown as IonInput).value as string;
  }

  isQuestionAnsweredInPreviousYear(question: SpartaxFrage, year: number | string, auftragskunde: SpartaxAuftragskunde) {
    const yearStr = year.toString();
    const erklaerungsjahrAlt = from(auftragskunde.ErklaerungsjahreAlt || []).firstOrDefault(x => x.Jahr.toString() === yearStr);
    if (erklaerungsjahrAlt) {
      return from(erklaerungsjahrAlt.FragenAntworten).where(x => !!x.Antwort).any(x => x.FrageListItemId === question.ListItemId && !!x.Antwort);
    }
  }

  getQuestionAnswer(question: SpartaxFrage, year: SpartaxErklaerungsjahr) {
    if (!year.FragenAntworten) {
      year.FragenAntworten = [];
    }

    let antwort = year.FragenAntworten.find(x => x.FrageListItemId === question.ListItemId);

    if (antwort) {
      return antwort.Antwort;
    } else {
      return false;
    }
  }

  getQuestionComment(question: SpartaxFrage, auftragskunde: SpartaxAuftragskunde) {
    if (!auftragskunde.Kommentare) {
      auftragskunde.Kommentare = [];
    }

    let kommentar = auftragskunde.Kommentare.find(x => x.FrageListItemId === question.ListItemId);

    if (kommentar) {
      return kommentar.Kommentar;
    }
  }

  onQuestionAnswered(question: SpartaxFrage, showNextQuestion: boolean) {
    if (!this.model.sonstigeFragenBeantwortet) {
      this.model.sonstigeFragenBeantwortet = [];
    }

    if (!this.model.sonstigeFragenBeantwortet.includes(question.ListItemId)) {
      this.model.sonstigeFragenBeantwortet.push(question.ListItemId);
    }

    setTimeout(() => {
      this.formEventService.triggerSubmitEvent(false);
    }, 0);

    if (showNextQuestion) {
      this.showNextQuestion(question);
    }
  }

  isQuestionAnswered(question: SpartaxFrage) {
    return this.model.sonstigeFragenBeantwortet && this.model.sonstigeFragenBeantwortet.includes(question.ListItemId);
  }

  isQuestionAnsweredPreviously(question: SpartaxFrage) {
    return this.model.sonstigeFragenBeantwortetAlt && this.model.sonstigeFragenBeantwortetAlt.includes(question.ListItemId);
  }

  markQuestionAsUnanswered(question: SpartaxFrage) {
    if (!this.model.sonstigeFragenBeantwortet) {
      this.model.sonstigeFragenBeantwortet = [];
    }

    const index = this.model.sonstigeFragenBeantwortet.indexOf(question.ListItemId);

    if (index >= 0) {
      this.model.sonstigeFragenBeantwortet.splice(index, 1);
    }

    setTimeout(() => {
      this.formEventService.triggerSubmitEvent(false);
    }, 0);
  }

  getAccordionHeaderBadgeCount(questionCategory: SpartaxFragenkategorie) {
    const questionsOfCategory = this.questionByCategoryId[questionCategory.ListItemId];

    let answerCount = 0;
    for (const question of questionsOfCategory) {
      if (this.isQuestionAnswered(question)) {
        answerCount++;
      }
    }

    return `${answerCount}/${questionsOfCategory.length}`;
  }

  isAnyQuestionOfCategoryAnsweredWithYes(questionCategory: SpartaxFragenkategorie) {
    const questionsOfCategory = this.questionByCategoryId[questionCategory.ListItemId];
    for (const question of questionsOfCategory) {
      for (const auftragskunde of this.model.auftragskunden) {
        if (this.isAuftragskundeRowVisible(question, auftragskunde)) {
          for (const erklärungsjahr of auftragskunde.Erklaerungsjahre) {
            if (this.getQuestionAnswer(question, erklärungsjahr)) {
              return true;
            }
          }
        }
      }
    }
  }

  isAnyQuestionOfCategoryAnsweredPreviously(questionCategory: SpartaxFragenkategorie) {
    const questionsOfCategory = this.questionByCategoryId[questionCategory.ListItemId];
    for (const question of questionsOfCategory) {
      if (this.isQuestionAnsweredPreviously(question)) {
        return true;
      }
    }
  }

  allQuestionsOfCategoryAnswered(questionCategory: SpartaxFragenkategorie) {
    for (const question of this.questionByCategoryId[questionCategory.ListItemId]) {
      if (!this.isQuestionAnswered(question)) {
        return false;
      }
    }

    return true;
  }

  showNextQuestion(currentQuestion: SpartaxFrage) {
    for (let i = 0; i < this.questionCategories.length; i++) {
      const questionsOfCategory = this.questionByCategoryId[this.questionCategories[i].ListItemId];
      for (let j = 0; j < questionsOfCategory.length; j++) {
        if (questionsOfCategory[j].ListItemId === currentQuestion.ListItemId) {
          //if last question of category => move to next category
          if (j + 1 === questionsOfCategory.length) {
            if (i + 1 === this.questionCategories.length) {
              return; //Do nothing on answering of last question
            } else {
              if (!this.cqMode) {
                this.categoriesAccordionGroup.value = this.questionCategories[i + 1].ListItemId.toString();
              }
              this.scrollToFirstQuestionOfCategory(this.questionCategories[i + 1]);
              return;
            }
          } else {
            this.scrollToQuestionRow(questionsOfCategory[j + 1]);
            return;
          }
        }
      }
    }
  }

  scrollToFirstQuestionOfCategory(questionCategory: SpartaxFragenkategorie) {
    const questionsOfCategory = this.questionByCategoryId[questionCategory.ListItemId];
    if (questionsOfCategory && questionsOfCategory.length > 0) {
      setTimeout(() => {
        this.scrollToQuestionRow(questionsOfCategory[0]);
      }, 750);
    }
  }

  scrollToQuestionRow(question: SpartaxFrage) {
    if (window.screen.width >= 1200) {
      const nextQuestionRow = document.querySelector("#beeex-question-divider-row-" + question.ListItemId);
      if (nextQuestionRow) {
        nextQuestionRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  onQuestionAccordionChange(event: Event) {
    if (event.target === event.currentTarget) {
      if (this.edit) {
        setTimeout(() => {
          this.formEventService.triggerSubmitEvent(false);
        }, 0);
      }

      if (this.categoriesAccordionGroup.value) {
        this.scrollToFirstQuestionOfCategory(this.questionCategories.find(x => x.ListItemId.toString() === this.categoriesAccordionGroup.value));
      }
    }
  }

  getAuftragskundenForQuestion(question: SpartaxFrage) {
    return this.model.auftragskunden.filter(x => this.isAuftragskundeRowVisible(question, x));
  }

  setInvisibleQuestionsToFalse() {
    for (const question of this.questions) {
      for (const auftragskunde of this.model.auftragskunden) {
        if (!this.isAuftragskundeRowVisible(question, auftragskunde)) {
          for (const erklärungsjahr of auftragskunde.Erklaerungsjahre) {
            this.setQuestionAnswer(false, question, erklärungsjahr);
          }
        } else {
          for (const erklärungsjahr of auftragskunde.Erklaerungsjahre) {
            if (!this.isYearRelevant(question, erklärungsjahr.Jahr, auftragskunde)) {
              this.setQuestionAnswer(false, question, erklärungsjahr);
            }
          }
        }
      }
    }
  }

  async showOvertakePopup(checked: boolean, question: SpartaxFrage, year: SpartaxErklaerungsjahr, auftragskunde: SpartaxAuftragskunde) {
    const key = `${auftragskunde.KundeFormModelId}_${question.ListItemId}`;
    if (this.overtakePromptsPresented.includes(key)) {
      return;
    } else {
      this.overtakePromptsPresented.push(key);
      const alert = await this.alertController.create({
        subHeader: 'Sollen die gewählte Antwort für die Folgejahre übernommen werden?',
        buttons: [
          {
            text: 'Nein',
            role: 'cancel',
          },
          {
            text: 'Ja',
            role: 'confirm',
            handler: () => {
              for (const erklärungsjahr of auftragskunde.Erklaerungsjahre) {
                if (erklärungsjahr.Jahr >= year.Jahr && this.isYearRelevant(question, erklärungsjahr.Jahr, auftragskunde)) {
                  this.setQuestionAnswer(checked, question, erklärungsjahr);
                }
              }
            },
          },
        ],
      });

      await alert.present();
      await alert.onDidDismiss();
    }
  }

  async showWiederaufnahmePopup(question: SpartaxFrage, year: SpartaxErklaerungsjahr, auftragskunde: SpartaxAuftragskunde) {
    let answer = false;
    const alert = await this.alertController.create({
      subHeader: 'Wiederaufnahme?',
      message: `Sind Sie sicher, dass die Frage für ${auftragskunde.Kunde.Vorname} im Jahr ${year.Jahr} wieder aufgerollt werden soll?`,
      buttons: [
        {
          text: 'Nein',
          role: 'cancel',
        },
        {
          text: 'Ja',
          role: 'confirm',
          handler: () => {
            answer = true;
          },
        },
      ],
    });

    await alert.present();
    await alert.onDidDismiss();

    //Automatically copy einkunftsarten und schätzung jahreseinkunft if wiederaufnahme
    if (answer) {
      if (!year.EinkunftsArten || year.EinkunftsArten.length === 0) {
        const erklaerungsjahrAlt = from(auftragskunde.ErklaerungsjahreAlt || []).firstOrDefault(x => x.Jahr === year.Jahr);
        if (erklaerungsjahrAlt) {
          year.EinkunftsArten = erklaerungsjahrAlt.EinkunftsArten;
          year.SchaetzungJahreseinkunft = erklaerungsjahrAlt.SchaetzungJahreseinkunft;
        }
      }
      year.Wiederaufnahme = true;
    }

    this.setQuestionAnswer(answer, question, year);
    //Because ion-checkbox maintains checked state, we need to set it to false and then to true
    this.checkboxStateOverride = true;
    setTimeout(() => {
      this.checkboxStateOverride = false;
    }, 0);
  }

  isMobile() {
    return window.innerWidth <= 770;
  }

  onCqAdditionalQuestionAnswered(question: SpartaxFrage, auftragskunde: SpartaxAuftragskunde, answer: boolean): void {
    if (!this.model.cqAdditionalQuestionsAnswers) {
      this.model.cqAdditionalQuestionsAnswers = {};
    }

    const key = `${question.ListItemId}_${auftragskunde.KundeFormModelId}`;
    this.model.cqAdditionalQuestionsAnswers[key] = answer;

    //Check if we can progress to the next question
    const allAuftragskundenOfQuestion = this.getAuftragskundenForQuestion(question);
    let allAnswered = true;
    for (const ak of allAuftragskundenOfQuestion) {
      const answerKey = `${question.ListItemId}_${ak.KundeFormModelId}`;
      if (this.model.cqAdditionalQuestionsAnswers[answerKey] === undefined) {
        allAnswered = false;
        break;
      }
    }

    if (allAnswered) {
      this.onQuestionAnswered(question, true);
    }
  }

  processCqAdditionalQuestionAnswers(): void {
    if (this.model.cqAdditionalQuestionsAnswers) {
      for (const key in this.model.cqAdditionalQuestionsAnswers) {
        const listItemIdStr = key.split('_')[0];
        const kundeFormModelId = key.split('_')[1];
        const question = from(this.questions).firstOrDefault(q => q.ListItemId.toString() === listItemIdStr);
        const auftragskunde = from(this.model.auftragskunden).firstOrDefault(ak => ak.KundeFormModelId === kundeFormModelId);
        const answer = this.model.cqAdditionalQuestionsAnswers[key];
        if (question && auftragskunde) {
          for (const year of auftragskunde.Erklaerungsjahre) {
            if (this.isYearRelevant(question, year.Jahr, auftragskunde)) {
              this.setQuestionAnswer(answer || false, question, year);
            }
          }
        }
      }
    }
  }
}
